import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Icon } from '@livable/saas-react-components';
import { Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { track, events } from 'src/configs/amplitude';
import { BillAutomationExtractedEditButton } from './BillAutomationExtractedEditButton';
import { formatExtractedMoney, formatUtilityLabel, getUtilityIconType } from './BillAutomationExtractedSummary';
import { useBillAutomationExtractedStyles } from './billAutomationExtractedStyles';
function formatLineItemValue(line, currency) {
  if (!line) return '—';
  const raw = line.amount;
  if (raw !== undefined && raw !== null && raw !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      return formatExtractedMoney(n, currency);
    }
  }
  if (line.quantity != null && line.unit) {
    const q = Number(line.quantity);
    const qty = Number.isFinite(q) ? q.toLocaleString('en-US') : String(line.quantity);
    return "".concat(qty, " ").concat(line.unit);
  }
  if (line.value != null && line.value !== '') return String(line.value);
  return '—';
}
function PropertyUtilityLineItems(_ref) {
  let currency = _ref.currency,
    prop = _ref.prop;
  const classes = useBillAutomationExtractedStyles();
  const services = prop.services || [];
  const _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    selectedIndex = _useState2[0],
    setSelectedIndex = _useState2[1];
  const page = 'Apply Billing';
  const safeIndex = services.length ? Math.min(selectedIndex, services.length - 1) : 0;
  const svc = services[safeIndex];
  const lineItems = (svc === null || svc === void 0 ? void 0 : svc.lineItems) || [];
  const subtotal = useMemo(() => {
    const items = (svc === null || svc === void 0 ? void 0 : svc.lineItems) || [];
    if ((svc === null || svc === void 0 ? void 0 : svc.subtotal) != null && Number.isFinite(Number(svc.subtotal))) {
      return formatExtractedMoney(svc.subtotal, currency);
    }
    const sum = items.reduce((acc, line) => {
      const n = Number(line === null || line === void 0 ? void 0 : line.amount);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
    return items.length ? formatExtractedMoney(sum, currency) : '—';
  }, [svc, currency]);
  if (!services.length) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.propertyCard
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsCardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsCardTitles
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.propertyTitle
  }, "Line items per utility"), /*#__PURE__*/React.createElement(Typography, {
    className: classes.propertyAddress
  }, prop.address || '—')), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsEditButtonWrap
  }, /*#__PURE__*/React.createElement(BillAutomationExtractedEditButton, {
    ariaLabel: "Edit line items"
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsShell
  }, /*#__PURE__*/React.createElement("nav", {
    className: classes.utilityNav,
    "aria-label": "Utilities on this bill"
  }, services.map((s, idx) => {
    const active = idx === safeIndex;
    return /*#__PURE__*/React.createElement("button", {
      key: "".concat(s.serviceType, "-").concat(idx),
      type: "button",
      className: "".concat(classes.utilityNavBtn, " ").concat(active ? classes.utilityNavBtnActive : ''),
      onClick: () => {
        track(events.selected('Extracted Utility'), {
          page,
          utility: formatUtilityLabel(s.serviceType)
        });
        setSelectedIndex(idx);
      },
      "aria-current": active ? 'true' : undefined,
      "aria-label": formatUtilityLabel(s.serviceType)
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.utilityNavIconBox
    }, /*#__PURE__*/React.createElement(Icon, {
      state: active ? 'dark' : 'grey',
      type: getUtilityIconType(s.serviceType)
    })));
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.utilityPanel
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.utilityPanelTitle
  }, formatUtilityLabel(svc.serviceType)), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsHeaderDivider
  }), lineItems.map((line, li) => /*#__PURE__*/React.createElement("div", {
    key: li,
    className: classes.lineItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemLabel
  }, line.description || line.category || '—'), /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemValue
  }, formatLineItemValue(line, currency)))), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsTotalRow
  }, /*#__PURE__*/React.createElement("span", null, "Total"), /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemsTotalValue
  }, subtotal)))));
}
export function BillAutomationExtractedLineItems(_ref2) {
  let data = _ref2.data;
  const classes = useBillAutomationExtractedStyles();
  if (!data) return null;
  const hasProperties = (data.properties || []).length > 0;
  const hasAllLineItems = (data.allLineItems || []).length > 0;
  if (!hasProperties && !hasAllLineItems) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.wrapper
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.sectionHeading
  }, "Below is the extracted bill detail by property:"), (data.properties || []).map((prop, idx) => /*#__PURE__*/React.createElement(PropertyUtilityLineItems, {
    key: prop.address || idx,
    currency: data.currency,
    prop: prop
  })), hasAllLineItems && !hasProperties ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.propertyCard, " ").concat(classes.fallbackLineItemsCard)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsCardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsCardTitles
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.propertyTitle
  }, "All line items")), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsEditButtonWrap
  }, /*#__PURE__*/React.createElement(BillAutomationExtractedEditButton, {
    ariaLabel: "Edit line items"
  }))), data.allLineItems.map((line, li) => /*#__PURE__*/React.createElement("div", {
    key: li,
    className: classes.lineItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemLabel
  }, line.description || line.category || '—'), /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemValue
  }, formatLineItemValue(line, data.currency))))) : null);
}